import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/api-client';
import { useFinanceView, type FinanceViewState } from '../useFinanceView';
import type { ActionContext } from './prima-nota-actions';
import { generate } from './prima-nota-generate';
import {
  isReadiness,
  previousPeriod,
  type PeriodSource,
  type Readiness,
  type StatementFormat,
} from './prima-nota-model';
import * as supply from './prima-nota-supply-actions';
import { useActionSlots, useDayFormats, useJudgement } from './usePrimaNotaState';

type Judgement = ReturnType<typeof useJudgement>;
type Slots = ReturnType<typeof useActionSlots>;
type ReadinessView = FinanceViewState<Readiness> & { reload: () => void };

/** The actions of the page, each bound to the month, the words and the page's own slots. */
function useBoundActions(
  ctx: ActionContext,
  view: ReadinessView,
  judgement: Judgement,
  slots: Slots,
) {
  const readiness = view.status === 'ready' ? view.data : null;

  const upload = (
    file: File,
    kind: PeriodSource['kind'],
    accountKey: string | null,
    format: StatementFormat | null = null,
  ) => supply.uploadSource(ctx, file, { kind, accountKey, format });

  const uploadProfile = (file: File) =>
    supply.uploadProfile(ctx, file, judgement.profileNote, () => {
      judgement.setProfileNote('');
      judgement.setRevisingProfile(false);
    });

  const withdraw = (sourceId: string) =>
    supply.withdrawSource(ctx, sourceId, slots.withdrawReason, () => {
      slots.setWithdrawing(null);
      slots.setWithdrawReason('');
    });

  return {
    upload,
    uploadProfile,
    withdraw,
    fetchDocuments: () => supply.fetchDocuments(ctx),
    sweepArchive: () => supply.sweepArchive(ctx),
    generate: () => generate(ctx, { readiness, latestJudgement: judgement.latestJudgement }),
  };
}

/** Everything the prima nota screen holds and does; the components only draw it. */
export function usePrimaNotaPage() {
  const { t, i18n } = useTranslation('finance');
  const [period, setPeriod] = useState(() => previousPeriod(new Date()));
  const [reviewed, setReviewed] = useState(false);
  const judgement = useJudgement();
  const slots = useActionSlots();

  const request = useCallback(
    () => apiClient.GET('/v1/finance/prima-nota/{period}', { params: { path: { period } } }),
    [period],
  );
  const view = useFinanceView(request, isReadiness);
  // Also locked while the month is re-read after an action: until the answer lands the page shows
  // the state from before, and an action started on it acts on what is no longer there.
  const locked = slots.busy !== null || (view.status === 'ready' && view.refreshing);

  const ctx: ActionContext = {
    period,
    t,
    setBusy: slots.setBusy,
    setMessage: slots.setMessage,
    setProduced: slots.setProduced,
    reload: view.reload,
  };
  const actions = useBoundActions(ctx, view, judgement, slots);
  const formats = useDayFormats(i18n.resolvedLanguage);

  /** Switching the month invalidates everything below it, including a register already produced. */
  const changePeriod = (next: string) => {
    setPeriod(next);
    slots.setProduced(null);
    slots.setMessage(null);
    setReviewed(false);
    judgement.clearForNewPeriod();
  };

  return {
    period,
    changePeriod,
    reviewed,
    setReviewed,
    judgement,
    slots,
    view,
    locked,
    actions,
    language: i18n.resolvedLanguage,
    ...formats,
  };
}

export type PrimaNotaPage = ReturnType<typeof usePrimaNotaPage>;
